/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { LinearProgress, Tooltip } from "@mui/material";
import { Column } from "data/table.data";
import {
  Dispatch,
  memo,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { PALETTE } from "styles/base/variables";
import { ReactComponent as IconChevronUp } from "assets/img/icon/chevron-sm-up.svg";
import BaseInput from "../Input/Base";
import { useSearchParams } from "react-router-dom";
import { alignToJustify, convertSortParamData } from "utils/converter";
import { MenuType, UpdateTxData } from "models/business.model";
import BaseCheck from "../Check/Base";
import { ExtendedOption } from "../Select/Base";
import { Close, HistoryOutlined } from "@mui/icons-material";
import IconButton from "../Button/Icon";
import LibraryAddOutlinedIcon from "@mui/icons-material/LibraryAddOutlined";
import { BankStatement } from "models/bank.model";
import { SubjectCodeData } from "models/subject.model";

interface TableProps {
  isLoading?: boolean;
  minWidth?: number | string;
  minHeight?: number | string;
  columns: Column[];
  idField?: string;
  sortLimit?: number;
  dataList: any[];
  subjectList?: SubjectCodeData[];
  highlightRowId?: string;
  holdingRowId?: string;
  selectable?: boolean;
  disableStatus?: {
    input?: boolean;
    doubleClick?: boolean;
    openDialog?: boolean;
    cancel?: boolean;
    restore?: boolean;
  };
  menuType?: MenuType;
  checkedSet?: Set<string>;
  setCheckedSet?: Dispatch<SetStateAction<Set<string>>>;
  modifiedMap?: Map<string, UpdateTxData>;
  loadData?: () => void;
  clickRow?: (data: any) => void;
}

interface TableBodyRowProps {
  index: number;
  data: any;
  chckSet: Set<string>;
  activeInput: { dataId: string | null; colId: string | null };
}

function Table({
  isLoading,
  minWidth = 1024,
  minHeight = 200,
  columns,
  idField = "id",
  sortLimit = 1,
  dataList,
  subjectList,
  highlightRowId,
  holdingRowId,
  selectable,
  disableStatus,
  menuType,
  checkedSet = new Set(),
  setCheckedSet,
  modifiedMap,
  loadData,
  clickRow,
}: TableProps) {
  const tdPx = 10;

  const [searchParams, setSearchParams] = useSearchParams();

  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);
  const [activeInput, setActiveInput] = useState<{
    dataId: string | null;
    colId: string | null;
  }>({
    dataId: null,
    colId: null,
  });
  const [lastShiftedIndex, setLastShiftedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (dataList.length && checkedSet.size === dataList.length)
      setIsCheckAll(true);
    else setIsCheckAll(false);
  }, [checkedSet, dataList]);

  const checkHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;

    if (checked) {
      checkedSet?.add(id);
    } else {
      checkedSet?.delete(id);
    }
    if (setCheckedSet) setCheckedSet(new Set(checkedSet));
  };

  const clickHandler = (e: React.MouseEvent<HTMLInputElement>, idx: number) => {
    const { checked } = e.target as HTMLInputElement;

    if (e.shiftKey && lastShiftedIndex !== null) {
      const start = Math.min(idx, lastShiftedIndex);
      const end = Math.max(idx, lastShiftedIndex);
      for (let i = start; i <= end; i++) {
        checkedSet?.add(dataList[i][idField]);
      }
    }

    if (checked) setLastShiftedIndex(idx);
    else setLastShiftedIndex(null); // 마지막 작업이 체크 해제 클릭일 경우 shift 클릭을 초기화
  };

  const checkAllHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    if (checked) {
      dataList.forEach((data) => {
        checkedSet?.add(data[idField]);
      });
    } else {
      dataList.forEach((data) => {
        checkedSet?.delete(data[idField]);
      });
    }
    if (setCheckedSet) setCheckedSet(new Set(checkedSet));
  };

  const sortHandler = (colId: string) => {
    const newSortData = convertSortParamData(searchParams.get("sort"), colId);
    let newSortList = Object.entries(newSortData);
    if (newSortList.length > sortLimit) {
      newSortList = newSortList.slice(1);
    }

    const newSortString = newSortList.map(([k, v]) => `${k},${v}`).join("|");
    searchParams.set("sort", newSortString);
    setSearchParams(searchParams);
  };

  const TableBodyRow = memo(
    ({ data, chckSet, activeInput, index }: TableBodyRowProps) => {
      const activeInputRef = useRef<HTMLInputElement>(null);

      const [options, setOptions] = useState<ExtendedOption[]>([]);

      useEffect(() => {
        if (activeInputRef.current && activeInput.dataId === data[idField]) {
          activeInputRef.current.focus();
        }
      }, [activeInputRef]);

      const isClickableRow = Boolean(clickRow);

      const clickableRowCss = `
        cursor: pointer;
        &:hover {
          background-color: ${PALETTE.background};
          
          td {
            font-weight: 600;
          }
        }
      `;

      const highlightRowCss = `
        background-color: ${PALETTE.primaryHover};
        font-weight: 500;
      `;

      const holdingRowCss = `
        background-color: ${PALETTE.background};
        font-weight: 500;
      `;

      return (
        <tr
          css={css`
            border-top: 1px solid ${PALETTE.borderLight};

            td {
              height: 48px;
              padding: 0 ${tdPx}px;

              &:first-of-type {
                border-radius: 0 0 0 8px;
              }
              &:last-child {
                border-radius: 0 0 8px 0;
              }
            }

            ${isClickableRow && clickableRowCss}
            ${highlightRowId &&
            data[idField] === highlightRowId &&
            highlightRowCss}
            ${holdingRowId && data[idField] === holdingRowId && holdingRowCss}
          `}
          onClick={isClickableRow ? () => clickRow?.(data) : undefined}
        >
          {selectable && (
            <td
              css={css`
                text-align: center;
              `}
            >
              <BaseCheck
                id={data[idField]}
                checked={chckSet.has(data[idField])}
                onChange={checkHandler}
                onClick={(e) => clickHandler(e, index)}
                css={css`
                  cursor: pointer;
                `}
              />
            </td>
          )}
          {columns.map((column, idx) => {
            const colId = column.id;
            const colSx = column.sx;
            const colWidth = colSx?.width;
            const isEditable =
              (column.input && !(disableStatus && disableStatus.input)) ||
              (column.doubleClick &&
                !(disableStatus && disableStatus.doubleClick));
            const isLink = Boolean(column.linkMaker);
            const isDialog = Boolean(column.openDialog);
            const tmpBankData = data as BankStatement;
            const isMultiAccountCode =
              tmpBankData.multiAccountCodes &&
              tmpBankData.multiAccountCodes.length > 0;

            let tmpData = { ...data };
            if (loadData) tmpData = { ...data, loadData };

            let isModifiedByBiz = false;
            if (colId === "isVatDeduction")
              isModifiedByBiz = data.lastModifiedBy === "biz";

            let changedVal;
            let isChanged = false;
            const modifiedCurrData = modifiedMap?.get(data[idField]);
            if (modifiedCurrData) {
              switch (colId) {
                case "isVatDeduction":
                case "accountCode":
                  changedVal = modifiedCurrData[colId]; // 변경된 값이 있으면 변경된 값으로 표시
                  isChanged = changedVal !== undefined;
                  break;
                case "accountName":
                  // 계정과목명 칼럼은 계정과목 코드 베이스 변경이라 코드를 변경해줘야 함
                  const modifiedAccCode = modifiedCurrData.accountCode;
                  if (modifiedAccCode !== undefined) {
                    isChanged = true;
                    if (subjectList) {
                      changedVal =
                        subjectList.find(
                          (subj) => subj.code === modifiedAccCode
                        )?.name || "";
                    }
                  }
                  break;
                case "accSplCft":
                case "accVaTxamt":
                  const isDdc = modifiedCurrData.isVatDeduction;
                  if (isDdc !== undefined) {
                    isChanged = true;
                    if (colId === "accSplCft")
                      changedVal = isDdc
                        ? data.splCft
                        : data.splCft + data.vaTxamt;
                    else if (colId === "accVaTxamt")
                      changedVal = isDdc ? data.vaTxamt : 0;
                  }
                  break;
              }

              if (isChanged) tmpData[colId] = changedVal;
            }

            const isActiveInput =
              column.input &&
              activeInput.dataId === data[idField] &&
              activeInput.colId === colId;
            const isCancellable = Boolean(column.cancel);
            const defaultVal = colId === "accountCode" ? "" : tmpData[colId]; // 계정과목 선택일 땐 빈값으로 시작

            const isTooltip = Boolean(column.tooltip);

            const filterOptions = (value: string) => {
              const filtered = column.input?.options?.filter((option) =>
                option.label.includes(value)
              );
              setOptions(filtered || []);
            };

            const keyDownHanlder = (
              e: React.KeyboardEvent<HTMLInputElement>
            ) => {
              const event = e as React.KeyboardEvent<HTMLInputElement>;
              const eventKey = event.key;
              const eventTarget = event.target as HTMLInputElement;
              const tgtValue = eventTarget?.value;

              if (eventKey === "Enter") {
                if (column.input?.options) {
                  if (tgtValue) {
                    const firstMatch = column.input.options.find((subject) =>
                      subject.label.includes(tgtValue)
                    );

                    if (firstMatch) {
                      column.input.submit(
                        firstMatch.value.toString(),
                        data[idField],
                        colId
                      );
                    }
                  }
                } else {
                  column.input?.submit(tgtValue, data[idField], colId);
                }
                setActiveInput({ dataId: null, colId: null });
              } else if (eventKey === "Escape") {
                setActiveInput({ dataId: null, colId: null });
              }
            };

            const selectOptionHandler = (
              e: React.FormEvent<HTMLInputElement>
            ) => {
              const event = e as React.FormEvent<HTMLInputElement>;
              const eventTarget = event.target as HTMLInputElement;
              const tgtValue = eventTarget?.value;

              filterOptions(tgtValue);
            };

            const widthCss = `width: ${colWidth + "px"};`;
            const sxCss = `
              text-align: ${colSx?.textAlign || "left"};
              word-break: ${colSx?.wordBreak || "keep-all"};
              white-space: ${colSx?.whiteSpace || "normal"};
            `;
            const editableCss = `
              cursor: pointer;
              &:hover {
                background-color: ${PALETTE.primaryHover};
              }
              span {
                color: ${PALETTE.textMid};
                font-weight: 700;
              }
            `;
            const changedCss = `
              span {
                background: linear-gradient(to top, yellow 50%,transparent 50%);
              }
            `;
            const changedByBiz = `
              background-color: #d3f9d8;
            `;
            const linkCss = `
              width: 100%;
              height: 100%;
              display: block;
              cursor: pointer;
              
              a {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                color: ${PALETTE.textMid};
                font-weight: 700;
                text-decoration: none;
              }

              &:hover {
                a {
                  color: ${PALETTE.primary};
                }
              }
            `;

            const closeButton = () => {
              return (
                <IconButton
                  onClick={() => setActiveInput({ dataId: null, colId: null })}
                >
                  <Close
                    css={css`
                      font-size: 15px;
                    `}
                  />
                </IconButton>
              );
            };

            return (
              <td
                key={colId + "_" + idx}
                css={css`
                  position: relative;
                  ${colWidth && widthCss}
                  ${colSx && sxCss}
                  ${isEditable && !isMultiAccountCode && editableCss}
                  ${isChanged && changedCss}
                  ${isModifiedByBiz && changedByBiz}
                  ${isLink && linkCss}
                `}
                onDoubleClick={
                  column.input &&
                  !isMultiAccountCode &&
                  !(disableStatus && disableStatus.input)
                    ? () =>
                        setActiveInput({
                          dataId: tmpData[idField],
                          colId: colId,
                        })
                    : column.doubleClick &&
                      !(disableStatus && disableStatus.doubleClick)
                    ? () =>
                        column.doubleClick?.submit(
                          tmpData[colId],
                          tmpData[idField]
                        )
                    : undefined
                }
              >
                {isActiveInput ? (
                  <div
                    css={css`
                      margin-left: -12px;
                      margin-right: -16px;
                      position: relative;
                    `}
                  >
                    <BaseInput
                      ref={activeInputRef}
                      type="text"
                      defaultValue={defaultVal}
                      onKeyDown={keyDownHanlder}
                      onFocus={column.input?.options && selectOptionHandler}
                      onInput={column.input?.options && selectOptionHandler}
                      endAdornment={closeButton()}
                      css={css`
                        width: 53px;
                        padding: 8px;
                      `}
                    />
                    {options && (
                      <div
                        css={css`
                          width: 175%;
                          max-height: 240px;
                          overflow-y: auto;
                          position: absolute;
                          background-color: ${PALETTE.background};
                          z-index: 100;
                        `}
                      >
                        {options.map((option, idx) => (
                          <div
                            key={idx}
                            onClick={() => {
                              column.input?.submit(
                                option.value.toString(),
                                tmpData[idField],
                                colId
                              );
                              setActiveInput({ dataId: null, colId: null });
                            }}
                            css={css`
                              padding: 8px 12px;
                              &:hover {
                                background-color: ${PALETTE.primaryHover};
                              }
                            `}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : isTooltip ? (
                  <Tooltip
                    title={column.tooltip?.(tmpData) || ""}
                    placement="top"
                    arrow
                  >
                    <span
                      css={css`
                        ${colSx?.fontSize &&
                        `font-size: ${colSx.fontSize + "px"};`}
                        ${colSx?.fontWeight &&
                        `font-weight: ${colSx.fontWeight};
                        `}
                      `}
                    >
                      {column.linkMaker?.(tmpData, menuType) ||
                        column.renderer?.(tmpData) ||
                        tmpData[colId] ||
                        "-"}
                    </span>
                  </Tooltip>
                ) : (
                  <span
                    css={css`
                      ${colSx?.fontSize &&
                      `font-size: ${colSx.fontSize + "px"};`}
                      ${colSx?.fontWeight &&
                      `font-weight: ${colSx.fontWeight};
                        `}
                    `}
                  >
                    {column.linkMaker?.(tmpData, menuType) ||
                      column.renderer?.(tmpData) ||
                      tmpData[colId] ||
                      "-"}
                  </span>
                )}
                {isDialog &&
                !isActiveInput &&
                !(
                  disableStatus &&
                  (disableStatus.input || disableStatus.doubleClick)
                ) ? (
                  <div
                    css={css`
                      position: absolute;
                      top: 4px;
                      right: -10px;
                      height: 100%;
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <IconButton
                      disabled={disableStatus?.openDialog}
                      onClick={() => column.openDialog?.(tmpData)}
                      css={css`
                        ${disableStatus?.openDialog && "cursor: not-allowed;"}
                      `}
                    >
                      <LibraryAddOutlinedIcon
                        css={css`
                          transform: scale(0.75);
                        `}
                      />
                    </IconButton>
                  </div>
                ) : null}
                {isCancellable ? (
                  <div
                    css={css`
                      position: absolute;
                      top: 0;
                      right: -6px;
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <IconButton
                      disabled={disableStatus?.cancel}
                      onClick={() => column.cancel?.(tmpData)}
                      css={css`
                        ${disableStatus?.cancel && "cursor: not-allowed;"}
                      `}
                    >
                      <Close
                        css={css`
                          font-size: 15px;
                        `}
                      />
                    </IconButton>
                  </div>
                ) : null}
                {isChanged && isEditable && !isActiveInput && column.restore ? (
                  <div
                    css={css`
                      position: absolute;
                      top: 0;
                      right: -6px;
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <IconButton
                      disabled={
                        disableStatus?.restore || data[idField] === undefined
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        column.restore?.({
                          dataId: data[idField],
                          colId: colId,
                        });
                      }}
                      css={css`
                        ${disableStatus?.restore && "cursor: not-allowed;"}
                      `}
                    >
                      <HistoryOutlined
                        css={css`
                          font-size: 15px;
                        `}
                      />
                    </IconButton>
                  </div>
                ) : null}
              </td>
            );
          })}
        </tr>
      );
    }
  );

  return (
    <div
      css={css`
        max-height: calc(100vh - 360px);
        min-height: ${typeof minHeight === "number"
          ? minHeight + "px"
          : minHeight};
        border: 1px solid ${PALETTE.border};
        border-radius: 8px;
        overflow-x: auto;
        position: relative;
      `}
    >
      {isLoading && (
        <div
          css={css`
            width: 100%;
            position: absolute;
            top: -1px;
            z-index: 100;
          `}
        >
          <LinearProgress
            sx={{
              backgroundColor: PALETTE.primaryBackground,
              "& .MuiLinearProgress-bar": {
                backgroundColor: PALETTE.primary,
              },
            }}
          />
        </div>
      )}
      <table
        css={css`
          width: 100%;
          min-width: ${typeof minWidth === "number"
            ? minWidth + "px"
            : minWidth};
          position: relative;
          overflow-x: hidden;
          border-collapse: collapse;
          font-size: 15px;
        `}
      >
        <thead
          css={css`
            background-color: ${PALETTE.background};
            top: 0;
            position: sticky;
            z-index: 10;
          `}
        >
          <tr
            css={css`
              th {
                height: 40px;
                padding: 0 ${tdPx}px;
                font-weight: 500;
                word-break: keep-all;

                &:first-of-type {
                  border-radius: 0 0 0 8px;
                }
                &:last-child {
                  border-radius: 0 0 8px 0;
                }
              }
            `}
          >
            {selectable && (
              <th
                css={css`
                  width: 24px;
                `}
              >
                <BaseCheck
                  id="all"
                  checked={isCheckAll}
                  onChange={checkAllHandler}
                  css={css`
                    cursor: pointer;
                  `}
                />
              </th>
            )}
            {columns.map((column, idx) => {
              const colId = column.id;
              const colSx = column.sx;
              const colWidth = colSx?.width;
              const widthCss = `
                width: ${colWidth + "px"};
                min-width: ${colWidth + "px"};
                max-width: ${colWidth + "px"};
              `;

              return (
                <th
                  key={colId + "_" + idx}
                  onClick={
                    column.sorting ? () => sortHandler(colId) : undefined
                  }
                  css={css`
                    ${colWidth && widthCss}
                    cursor: ${column.sorting ? "pointer" : "default"};
                  `}
                >
                  <span
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: ${alignToJustify(colSx?.textAlign)};
                      column-gap: 4px;
                    `}
                  >
                    {column.label}
                    {convertSortParamData(
                      searchParams.get("sort"),
                      colId,
                      true
                    )[colId] ? (
                      <IconChevronUp
                        css={css`
                          transform: ${convertSortParamData(
                            searchParams.get("sort"),
                            colId
                          )[colId] === "desc"
                            ? "rotate(0deg)"
                            : "rotate(180deg)"};
                        `}
                      />
                    ) : (
                      column.sorting && (
                        <div
                          css={css`
                            margin-left: 2px;
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                          `}
                        >
                          <IconChevronUp
                            css={css`
                              width: 12px;
                              transform: translateY(4px);
                            `}
                          />
                          <IconChevronUp
                            css={css`
                              width: 12px;
                              transform: translateY(-4px) rotate(180deg);
                            `}
                          />
                        </div>
                      )
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody
          css={css`
            background-color: ${PALETTE.light};
          `}
        >
          {dataList.length ? (
            dataList.map((data, index) => (
              <TableBodyRow
                key={index}
                index={index}
                data={data}
                chckSet={checkedSet}
                activeInput={activeInput}
              />
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                css={css`
                  border-top: 1px solid ${PALETTE.border};
                  text-align: center;
                  font-size: 16px;
                  font-weight: 500;
                `}
              >
                <div
                  css={css`
                    height: 100%;
                    min-height: 160px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                  `}
                >
                  {isLoading
                    ? "데이터를 확인하는 중입니다."
                    : "데이터가 없습니다."}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
